/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-await-in-loop */

import AssertionError from 'assertion-error';
import isNil from 'lodash/isNil';

import PromiseUtil from '../../promise/promise-util';
import ScheduleContent from '../../schedule/schedule-content';
import ViewerDelayerFactory from '../../viewer/delayer/viewer-delayer-factory';
import { BaseContentController } from './base-content-controller';
import { createContentController } from './content-controller-util';
import {
  RealtimePlaylistSimulator,
  SimulatorUnrecoverableError
} from './playlist-simulator';

/**
 * @import { Time } from '@ki-wi/deterministic'
 * @import { ContentController, CreateContentControllerDeps, Spot, SpotContent } from '.'
 * @import { Logger } from '../../logger/logger'
 * @import PlaybackControlsEmitter from '../../playback-controls/playback-controls-emitter'
 * @import { PreloadStrategy } from '../../preload-strategy'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import { IVariablesNamespaceRegistry } from '../../variables'
 * @import { Content } from '../content';
 * @import { ContentInfo } from '../info/content-info';
 * @import ContentControllerFactory from './content-controller-factory'
 */

/**
 * @typedef {{spot: Spot<SpotContent | null>; contentController: ContentController}} RealtimeContent
 * @typedef {{content: RealtimeContent; preloaded: boolean;}} NextRealtimeContent
 */

/**
 * @implements {ContentController}
 */
export class PlaylistContentController extends BaseContentController {
  /**
   * @param {Content} content
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {boolean} disableAutorewind
   * @param {Logger} logger
   * @param {PreloadStrategy} preloadStrategy
   * @param {HTMLElement} parentEl
   * @param {number} parentVolume
   * @param {PlaybackControlsEmitter} playbackControlsEmitter
   * @param {UnixTimestamp} unixTimestamp
   * @param {IVariablesNamespaceRegistry} variablesNamespaceRegistry
   */
  constructor(
    content,
    contentControllerFactory,
    contentInfo,
    disableAutorewind,
    logger,
    preloadStrategy,
    parentEl,
    parentVolume,
    playbackControlsEmitter,
    unixTimestamp,
    variablesNamespaceRegistry
  ) {
    const playlist = content.playlist;
    if (!playlist || playlist.deterministic) {
      throw new Error('content should be non deterministic playlist');
    }

    super();
    this.disableAutorewind = disableAutorewind;
    this.logger = logger;
    this.preloadStrategy = preloadStrategy;
    this.parentVolume = parentVolume;
    this.playbackControlsEmitter = playbackControlsEmitter;
    this.unixTimestamp = unixTimestamp;
    this.variablesNamespaceRegistry = variablesNamespaceRegistry;

    /** @type {AbortController | undefined} */
    this.abortController = undefined;
    this.delayer = new ViewerDelayerFactory({ delay: 1000, limit: 5 }).create();
    /** @type {CreateContentControllerDeps} */
    this.createContentControllerDeps = {
      contentControllerFactory,
      contentInfo,
      el: parentEl
    };
    this.loopQueue = Promise.resolve();
    /** @type {RealtimeContent | undefined} */
    this.nextRealtimeContent = undefined;
    this.playlist = playlist;
    /** @type {Spot<SpotContent | null> | undefined} */
    this.prevSpot = undefined;
    /** @type {(() => void) | undefined} */
    this.removePictogram = undefined;
    /** @type {RealtimePlaylistSimulator | undefined} */
    this.simulator = undefined;

    this.now = this.now.bind(this);
  }

  /**
   * @returns {RealtimePlaylistSimulator}
   */
  createSimulator() {
    return new RealtimePlaylistSimulator(
      this.disableAutorewind,
      () => /** @type {Time} */ (new Date().getTime() * 1000),
      this.playlist,
      this.parentVolume,
      this.unixTimestamp,
      this.variablesNamespaceRegistry
    );
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(async () => {
      this.abortController = await new AbortController();
      this.loopQueue = Promise.resolve();
      const simulator = this.createSimulator();
      this.simulator = simulator;

      const spot = await simulator.peek();
      if (!spot || !spot.content) {
        return;
      }
      const contentController = createContentController(
        spot,
        this.createContentControllerDeps
      );

      try {
        await contentController.preload();
        this.nextRealtimeContent = { spot, contentController };
        // Umýslné ignorování error, protože např. nemusí být
        // medium stažené, takže preload neuspěje.
      } catch {}
    });
  }

  /** @type {ContentController['restart']} */
  async restart() {
    await super.restartCallback(async () => {
      // Ukončení aktuálně přehrávaného playlistu.
      this.abortController?.abort();
      // Prvně se musí počkat na dokončení přehrávací smyčky.
      await this.loopQueue.catch(() => {});

      this.abortController = await new AbortController();
      this.loopQueue = Promise.resolve();
      this.simulator = await this.createSimulator();
      this.startPlaylistLoop(this.abortController);
    });
  }

  /**
   * @param {AbortController} abortController
   */
  startPlaylistLoop(abortController) {
    (async () => {
      while (!abortController.signal.aborted) {
        try {
          this.loopQueue = this.startPlaylist(abortController).then((success) =>
            success || abortController.signal.aborted
              ? this.delayer.succeed()
              : this.delayer.fail()
          );
          await this.loopQueue;
        } catch (cause) {
          if (cause instanceof SimulatorUnrecoverableError) {
            this.logger.error(cause);
            return;
          }
          this.logger.error(
            new Error('PlaylistContentController#start()', { cause })
          );
          await this.delayer.fail();
          this.loopQueue = Promise.resolve();
        }
      }
    })();
  }

  /** @type {ContentController['start']} */
  async start() {
    const { abortController } = this;
    if (!abortController) {
      throw new AssertionError('AbortController not set');
    }

    // eslint-disable-next-line require-await
    await super.startCallback(async () => {
      // Spuštění asynchronně přehrávací smyčky.
      this.startPlaylistLoop(abortController);
    });
  }

  /**
   * Spustí jeden track playlistu a počká na jeho přehrání. Funkce vrací true,
   * pokud bylo přehrání úspěšné, jinak se vrací false.
   * @param {AbortController} abortController
   * @returns {Promise<boolean>}
   */
  async startPlaylist(abortController) {
    const { simulator } = this;
    if (!simulator) {
      throw new AssertionError('playlist simulator not set');
    }
    /** @type {RealtimeContent | undefined} */
    let realtimeContent;
    let hasPreloadedContent = false;
    /** @type {Content | undefined} */
    let contentModel;
    try {
      const next = await getNextRealtimeContent(
        simulator,
        this.nextRealtimeContent,
        (spot) =>
          createContentController(spot, this.createContentControllerDeps)
      );
      if (next === undefined) {
        // Nastal konec playlistu dosažením na konec simulačního času nebo skončila jedna iterace.
        abortController.abort();
        this.emitter.emit('ended');
        return false;
      }
      contentModel = next?.content.spot.content?.model;
      if (next === null || isNil(contentModel)) {
        // Prázdný obsah.
        return false;
      }
      const { content, preloaded } = next;
      realtimeContent = content;
      hasPreloadedContent = preloaded;
    } catch (err) {
      if (err instanceof SimulatorUnrecoverableError) {
        throw err;
      }
      return false;
    }
    this.nextRealtimeContent = undefined;

    const { contentController, spot } = realtimeContent;
    const abortSignal = abortController.signal;

    /** @type {PromiseWithResolvers<boolean>} */
    const { promise: promiseError, resolve: resolveError } =
      Promise.withResolvers();
    /** @type {PromiseWithResolvers<boolean>} */
    const { promise: promiseEnded, resolve: resolveEnded } =
      Promise.withResolvers();
    /** @type {PromiseWithResolvers<boolean>} */
    const { promise: promiseAbort, resolve: resolveAborted } =
      Promise.withResolvers();
    /** @type {PromiseWithResolvers<boolean>} */
    const { promise: promiseSkip, resolve: resolveSkip } =
      Promise.withResolvers();

    const onAbort = () => resolveAborted(false);
    const onError = () => resolveError(false);
    const onEnded = () => resolveEnded(true);
    /** @type {(content: Content) => void} */
    const onskip = (content) => {
      // TODO: Pri layoute s viacerými playlistami s rovnakým ID sa stáva, že sa posunú oba -
      // vyriešila by to väzba na layout/layer.
      if (content.playlist?.id === this.playlist.id) {
        return resolveSkip(true);
      }
    };

    /** @type {(content: Content) => void} */
    const onPlayed = (content) => {
      if (ScheduleContent.isLeafContent(spot.content?.model)) {
        return;
      }
      // Emitování played eventů pouze pro nekoncové ContentController.
      this.emitter.emit('played', content);
    };
    /** @type {(content: Content) => void} */
    const onPlaying = (content) => {
      if (ScheduleContent.isLeafContent(spot.content?.model)) {
        return;
      }
      // Emitování playing eventů pouze pro nekoncové ContentController.
      this.emitter.emit('playing', content);
    };

    abortSignal.addEventListener('abort', onAbort);
    contentController.on('error', onError);
    contentController.on('ended', onEnded);
    contentController.on('played', onPlayed);
    contentController.on('playing', onPlaying);
    this.playbackControlsEmitter.on('skip', onskip);

    /** @type {ReturnType<setTimeout> | undefined} */
    let trackTimerId;
    /** @type {Promise<boolean>} */
    const promiseTimer = new Promise((resolve) => {
      if (spot.duration !== 0) {
        trackTimerId = setTimeout(() => resolve(true), spot.duration / 1000);
      }
    });

    try {
      if (!hasPreloadedContent) {
        await contentController.preload();
      }
      await contentController.start(0);
      this.emitter.emit('playing', contentModel);
    } catch (err) {
      // Případné chyby se ignorují.
      return false;
    }

    // Přednačtení dalšího media v pořadí.
    await simulator
      .peek()
      .then(async (nextSpot) => {
        const prev = this.prevSpot?.content?.model;
        const curr = contentModel;
        const next = nextSpot?.content?.model;
        if (
          !(next && prev && this.preloadStrategy.canPreload(next, curr, prev))
        ) {
          return;
        }

        const nextContentController = createContentController(
          nextSpot,
          this.createContentControllerDeps
        );

        // Délka zpoždění byla zjištěná experimentálně na TV. Zpoždění jen a pouze kvůli webOS Signage 4.0 a 4.1,
        // kde docházelo k černání videí po kterých následoval obrázek. Zvuk videí na pozadí hrál, fungovalo onended.
        // Jediné, co dokázalo zčernání videa opravit bylo kliknutí na libovolný element v DOM přes DevTools.
        // Zpoždění teoreticky stačí 100 ms, ale po vzoru zaniklého ViewerThred, necháme 200 ms.
        await PromiseUtil.wait(200);
        await nextContentController.preload();
        // Nastavuje se až po úspěšném volání preload.
        this.nextRealtimeContent = {
          spot: nextSpot,
          contentController: nextContentController
        };
      })

      // Preload se nepovedl, např. kvůli vyčerpání aktuálně vypůjčených zdrojů.
      .catch(() => {});

    return await Promise.race([
      // Časovač podle délky tracku.
      promiseTimer,
      // Reakce na error. Pokud nastane chyba v přehrávání obsahu, tak přerušujeme přehrvání.
      promiseError,
      // Reakce na ended event. Pokud je fyzická délka obsahu kratší než délka tracku.
      promiseEnded,
      // Reakce na vynucené ukončení přehrvání obsahu.
      promiseAbort,
      // User-driven forward step (skip current track).
      promiseSkip
    ]).finally(async () => {
      this.emitter.emit('played', contentModel);
      // Umyslné ignorování chyb.
      await contentController.stop().catch(() => {});
      this.prevSpot = spot;

      clearTimeout(trackTimerId);
      abortSignal.removeEventListener('abort', onAbort);
      contentController.off('error', onError);
      contentController.off('ended', onEnded);
      contentController.off('played', onPlayed);
      contentController.off('playing', onPlaying);
      this.playbackControlsEmitter.off('skip', onskip);
    });
  }

  /** @type {ContentController['stop']} */
  async stop() {
    try {
      await super.stopCallback(async () => {
        this.abortController?.abort();
        // Prvně se musí počkat na dokončení přehrávací smyčky.
        await this.loopQueue.catch(() => {});

        this.delayer.destroy();
        await this.nextRealtimeContent?.contentController
          .stop()
          .catch(() => {});
        this.nextRealtimeContent = undefined;
        this.prevSpot = undefined;
        this.loopQueue = Promise.resolve();
      });
    } catch {
      // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
      // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
    }
  }

  /**
   * Zjistí aktuální čas.
   * @private
   * @returns {number} Aktuální čas v mikrosekundách.
   */
  now() {
    return this.unixTimestamp.now() * 1000;
  }
}

/**
 * Vrací další přehrávaný obsah v pořadí. Pokud se nextRealtimeContent shoduje
 * s aktuálně získaným obsahem ze simulátoru, tak je vrácen s preloaded === true.
 * Jinak se vrací obsah ze simulátoru s preloaded === false. Null je vráceno pokud
 * byl vrácen prázdný obsah. Undefined je vráceno, pokud playlist dorazil na konec.
 * @param {RealtimePlaylistSimulator} simulator
 * @param {RealtimeContent | undefined} nextRealtimeContent
 * @param {(spot: Spot<SpotContent | null>) =>  ContentController} create
 * @returns {Promise<NextRealtimeContent | null | undefined>}
 */
async function getNextRealtimeContent(simulator, nextRealtimeContent, create) {
  /** @type {Spot<SpotContent | null> | undefined} */
  let s;
  try {
    s = await simulator.next();
  } catch (cause) {
    throw new SimulatorUnrecoverableError('RealtimePlaylistSimulator#next()', {
      cause
    });
  }

  if (s === undefined || (s.content === null && s.duration === 0)) {
    // Konec simulačního času nebo konec jedné iterace playlistu.
    return undefined;
  }
  if (s.content === null) {
    // Prázdný obsah.
    return null;
  }
  /**
   * @param {Spot<SpotContent | null>} spot
   * @returns {NextRealtimeContent}
   */
  const newNextRealtimeContent = (spot) => {
    const contentController = create(spot);
    return { content: { spot, contentController }, preloaded: false };
  };

  if (!nextRealtimeContent) {
    return newNextRealtimeContent(s);
  }

  const c1 = nextRealtimeContent.spot.content?.model;
  const c2 = s.content.model;
  // Mám preloadovaný content, nutné ověřit jestli byl preload správný.
  if (ScheduleContent.isSameContent(c1, c2)) {
    const { contentController, spot } = nextRealtimeContent;
    return { content: { contentController, spot }, preloaded: true };
  }
  // Preload by špatný. Unloadni preloadnutý obsah.
  // Umyslné ignorování chyb.
  await nextRealtimeContent.contentController.stop().catch(() => {});
  // Vrať správný obsah.
  return newNextRealtimeContent(s);
}
